import type { ApiLogicFilter } from '../../../../internalTypes';
import type { ApiConditionEntity } from '../../../../internalTypes/apiControlConfig';
import type {
  CustomFieldType,
  LogicConditionEntity,
  LogicFilterGroup,
  LogicFilterLeaf,
  LogicFilterNode,
  LogicSubConditionEntity,
} from '../../../../types/survey';
import type { ApiSubConditionEntity } from '../../../../internalTypes/apiControlConfig';

type ApiFilterGroup = ApiLogicFilter & { filters: ApiLogicFilter[] };

const CUSTOM_FIELD_TYPES = new Set<CustomFieldType>([
  'TEXT',
  'TEXT_MULTI',
  'TEXTAREA',
  'NUMBER',
  'PICKLIST',
  'PICKLIST_MULTISELECT',
  'DATETIME',
]);

function hasNestedFilters(filter: ApiLogicFilter): filter is ApiFilterGroup {
  return Array.isArray(filter.filters) && filter.filters.length > 0;
}

function isAndOrOperator(value: string | undefined): value is 'AND' | 'OR' {
  return value === 'AND' || value === 'OR';
}

function isCustomFieldType(value: string | undefined): value is CustomFieldType {
  return value !== undefined && CUSTOM_FIELD_TYPES.has(value as CustomFieldType);
}

function mapQuestionConditionEntity(
  entity: ApiConditionEntity | undefined
): Extract<LogicConditionEntity, { entityType: 'QUESTION' }> | null {
  if (!entity?.id || entity.entityType !== 'QUESTION') {
    return null;
  }

  return {
    id: entity.id,
    entityType: 'QUESTION',
    ...(entity.answerType ? { answerType: entity.answerType } : {}),
  };
}

function mapCustomFieldConditionEntity(
  entity: ApiConditionEntity | undefined
): Extract<LogicConditionEntity, { entityType: 'CUSTOM_FIELD' }> | null {
  if (!entity?.id || entity.entityType !== 'CUSTOM_FIELD' || !isCustomFieldType(entity.fieldType)) {
    return null;
  }

  return {
    id: entity.id,
    entityType: 'CUSTOM_FIELD',
    fieldType: entity.fieldType,
    ...(entity.assetClass ? { assetClass: entity.assetClass } : {}),
  };
}

function mapConditionEntity(entity: ApiConditionEntity | undefined): LogicConditionEntity | null {
  return mapQuestionConditionEntity(entity) ?? mapCustomFieldConditionEntity(entity);
}

function mapSubConditionEntity(
  entity: ApiSubConditionEntity | undefined
): LogicSubConditionEntity | null {
  if (!entity?.id || entity.entityType !== 'OPTION') {
    return null;
  }

  return {
    id: entity.id,
    entityType: 'OPTION',
  };
}

function mapLogicFilterLeaf(raw: ApiLogicFilter): LogicFilterLeaf | null {
  if (!raw.filterType || isAndOrOperator(raw.filterType)) {
    return null;
  }

  const conditionEntity = mapConditionEntity(raw.details?.conditionEntity);
  if (!conditionEntity) {
    return null;
  }

  const subConditionEntity = mapSubConditionEntity(raw.details?.subConditionEntity);

  const leaf: LogicFilterLeaf = {
    filterType: raw.filterType,
    details: {
      conditionEntity,
      ...(subConditionEntity ? { subConditionEntity } : {}),
    },
  };

  const timezone = raw.details?.valueAdditional?.timezone;
  if (timezone) {
    leaf.details!.valueAdditional = { timezone };
  }

  if (raw.field) {
    leaf.field = raw.field;
  }

  if (raw.values) {
    leaf.values = raw.values;
  }

  return leaf;
}

function mapLogicFilterGroupNode(raw: ApiFilterGroup): LogicFilterGroup | null {
  if (!isAndOrOperator(raw.filterType)) {
    return null;
  }

  const childFilters = raw.filters
    .map(child => mapLogicFilterNode(child))
    .filter((node): node is LogicFilterNode => node !== null);

  if (childFilters.length === 0) {
    return null;
  }

  return {
    filterType: raw.filterType,
    filters: childFilters,
  };
}

export function mapLogicFilterNode(raw: ApiLogicFilter): LogicFilterNode | null {
  if (hasNestedFilters(raw)) {
    return mapLogicFilterGroupNode(raw);
  }

  return mapLogicFilterLeaf(raw);
}

function isLogicFilterGroup(node: LogicFilterNode): node is LogicFilterGroup {
  return 'filters' in node;
}

export function mapLogicFilterGroup(raw: ApiLogicFilter | undefined): LogicFilterGroup | null {
  if (!raw) {
    return null;
  }

  const node = mapLogicFilterNode(raw);
  if (!node) {
    return null;
  }

  if (isLogicFilterGroup(node)) {
    return node;
  }

  // A single comparison rule is stored as a one-item AND group.
  return {
    filterType: 'AND',
    filters: [node],
  };
}
